import { useSafeLayoutEffect, useSize } from '@hover/blueprint';
import type { RefObject } from 'react';
import { useCallback, useState } from 'react';

import { useMutationObserver } from 'src/shared/hooks';

export type Direction = 'horizontal' | 'vertical';

export type Ref = RefObject<HTMLElement>;

export interface IsScrollableOptions {
  /**
   * Scroll direction
   */
  direction: Direction;
  /**
   * Ref for the element that is being scrolled
   */
  ref: Ref;
  /**
   * Value to initialize scrollable state to before we're actually able to
   * determine it from the DOM.
   *
   * @remarks This is usually only relevant for SSR where it allows you to
   * determine the state of the UI responding to this value before hydration.
   *
   * @default false
   */
  initial?: boolean;
}

type ElementSize = ReturnType<typeof useSize>;

const getScrollDimensions = (ref: Ref) => {
  const {
    scrollHeight = 0,
    scrollWidth = 0,
    clientHeight = 0,
    clientWidth = 0,
  } = ref.current ?? {};

  return {
    scrollHeight,
    scrollWidth,
    clientHeight,
    clientWidth,
  };
};

export type ScrollSize = Exclude<ElementSize, undefined> & {
  scrollHeight: number;
  scrollWidth: number;
};

export type IsScrollableReturn = readonly [
  scrollable: boolean,
  size: ScrollSize | undefined,
];

export const useIsScrollable = ({
  direction,
  ref,
  initial = false,
}: IsScrollableOptions): IsScrollableReturn => {
  const [isScrollable, setIsScrollable] = useState(initial);

  const size = useSize(ref);

  const check = useCallback(() => {
    const { scrollHeight, scrollWidth, clientHeight, clientWidth } =
      getScrollDimensions(ref);

    setIsScrollable(
      direction === 'horizontal'
        ? scrollWidth > clientWidth
        : scrollHeight > clientHeight,
    );
  }, [direction, ref]);

  // Recompoute scrollable state when the scroll content changes
  useMutationObserver({
    ref,
    callback: check,
  });

  const { scrollHeight, scrollWidth, clientHeight, clientWidth } =
    getScrollDimensions(ref);

  // Recompute scrollable state when the size of the scroll container changes
  useSafeLayoutEffect(() => {
    check();
  }, [
    size?.width,
    size?.height,
    clientHeight,
    clientWidth,
    scrollHeight,
    scrollWidth,
    check,
  ]);

  return [
    isScrollable,
    size ? { ...size, scrollHeight, scrollWidth } : undefined,
  ] as const;
};
